// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Burn,
  BurnRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Burn';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import { useAcceptBurnRequest } from '../../../hooks/mutations/registry/burn/useAcceptBurnRequest';
import { useCancelBurnRequest } from '../../../hooks/mutations/registry/burn/useCancelBurnRequest';
import { useRejectBurnRequest } from '../../../hooks/mutations/registry/burn/useRejectBurnRequest';
import useAcceptedBurns from '../../../hooks/queries/registry/burn/useAcceptedBurns';
import useBurnOffers from '../../../hooks/queries/registry/burn/useBurnOffers';
import useBurnRequests from '../../../hooks/queries/registry/burn/useBurnRequests';
import useExecutedBurns from '../../../hooks/queries/registry/burn/useExecutedBurns';
import useFailedBurns from '../../../hooks/queries/registry/burn/useFailedBurns';
import useRejectedBurns from '../../../hooks/queries/registry/burn/useRejectedBurns';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { fmt } from '../../../utils/common';
import { ERR_ACC_BURN, ERR_REJ_BURN } from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import { RequestBurnDialog } from './RequestBurnDialog';

type Row = {
  contractId: string;
  state: string;
  payload: Burn;
  reason?: string;
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'state',
      headerName: 'State',
      renderCell: (params) => (
        <StateCell state={params.row.state as Status} reason={params.row.reason} />
      ),
      flex: 1,
      filterable: false,
    },
    {
      field: 'instrumentId',
      headerName: 'Instrument Id',
      valueGetter: (params) => params.row.payload.instrumentIdentifier.id,
      flex: 1,
      filterable: false,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      renderCell: (params) => fmt(params.row.payload.amount, 2),
      valueGetter: (params) => Number(params.row.payload.amount),
      flex: 1,
      filterable: false,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 1,
      filterable: false,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
      flex: 1,
      filterable: false,
    },
    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.payload.reference,
      flex: 1,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 1,
      filterable: false,
    },
  ];
};

export const Burns = () => {
  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const { party } = useParties();
  const registrarService = useRegistrarService();
  const registrarServices = useRegistrarServices();
  const burnRequests = useBurnRequests();
  const burnOffers = useBurnOffers();
  const acceptedBurns = useAcceptedBurns();
  const rejectedBurns = useRejectedBurns();
  const executedBurns = useExecutedBurns();
  const failedBurns = useFailedBurns();
  const holdings = useHoldings();
  const acceptBurnRequest = useAcceptBurnRequest();
  const rejectBurnRequest = useRejectBurnRequest();
  const cancelBurnRequest = useCancelBurnRequest();

  const { showBoundary } = useErrorBoundary<Error>();
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const acceptBurn = useCallback(
    async (r: Row) => {
      if (!registrarService.data) {
        showBoundary(new Error(ERR_ACC_BURN, { cause: 'Registrar services are not available.' }));
        return Promise.resolve();
      }
      const registrarServiceCid = registrarService.data.contractId;

      return acceptBurnRequest({
        cid: registrarServiceCid,
        argument: { cid: r.contractId as ContractId<BurnRequest>, payload: {} },
      });
    },
    [registrarService, acceptBurnRequest, showBoundary],
  );

  const rejectBurn = useCallback(
    (r: Row, reason: string) => {
      if (!registrarService.data) {
        showBoundary(new Error(ERR_REJ_BURN, { cause: 'Registrar services are not available.' }));
        return Promise.resolve();
      }

      return rejectBurnRequest({
        cid: registrarService.data.contractId,
        argument: { cid: r.contractId as ContractId<BurnRequest>, payload: { reason } },
      });
    },
    [registrarService, rejectBurnRequest, showBoundary],
  );

  const cancelBurn = useCallback(
    async (r: Row) => {
      await cancelBurnRequest({ cid: r.contractId as ContractId<BurnRequest>, argument: {} });
    },
    [cancelBurnRequest],
  );

  const isLoading =
    registrarService.isLoading ||
    registrarServices.isLoading ||
    burnRequests.isLoading ||
    burnOffers.isLoading ||
    acceptedBurns.isLoading ||
    rejectedBurns.isLoading ||
    executedBurns.isLoading ||
    holdings.isLoading ||
    failedBurns.isLoading ||
    !burnRequests.data ||
    !burnOffers.data ||
    !acceptedBurns.data ||
    !rejectedBurns.data ||
    !executedBurns.data ||
    !failedBurns.data ||
    !holdings.data;
  if (isLoading) return <Loading />;

  const rows = burnRequests.data
    .map((r) => ({
      contractId: r.contractId as string,
      state: 'Requested',
      payload: r.payload.burn,
    }))
    .concat(
      burnOffers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Offered',
        payload: r.payload.burn,
      })),
    )
    .concat(
      acceptedBurns.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Accepted',
        payload: r.payload.burn,
      })),
    )
    .concat(
      rejectedBurns.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Rejected',
        payload: r.payload.burn,
      })),
    )
    .concat(
      executedBurns.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Executed',
        payload: r.payload.burn,
      })),
    )
    .concat(
      failedBurns.data.map((r) => ({
        contractId: r.contractId as string,
        state: `Failed: ${r.payload.reason}`,
        payload: r.payload.burn,
      })),
    );

  const isRegistrar = !!registrarService.data;
  const createBurnActions = (r: Row) => {
    const actions: Action[] = [];
    if (isRegistrar && r.state === 'Requested' && r.payload.registrar === party)
      actions.push(
        { name: 'Accept', action: (row: Row) => Promise.resolve(acceptBurn(row)) },
        {
          name: 'Reject',
          action: () => {
            setOnReject(() => (reason: string) => rejectBurn(r, reason));
            return Promise.resolve();
          },
        },
      );
    if (r.state === 'Requested' && r.payload.holder === party)
      actions.push({ name: 'Cancel', action: cancelBurn });
    return actions;
  };
  const buttons = [
    {
      name: 'request burn',
      onClick: () => setDialogName('burn'),
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Burns">
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}
      {dialogName === 'burn' && <RequestBurnDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={createHeaders(createBurnActions)}
        rows={rows}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
